import { accountLabel } from './accountLabel';
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Heading,
  Text,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  BALANCE_MODE,
  formatBalanceThousands,
  getHighlightDates,
  groupDailyBalancesByAccount,
  pickBalanceValue,
} from './accountBalanceChartHelpers';

const LINE_COLOR = '#01B574';
const LATEST_LINE_COLOR = '#1B254B';
const PREVIOUS_MONTH_LINE_COLOR = '#728fea';

function formatAxisDate(date) {
  if (!date) {
    return '';
  }
  const [year, month, day] = date.split('-');
  if (!year || !month || !day) {
    return date;
  }
  return `${month}/${day}`;
}

function AccountBalanceChart({ account, balanceMode, formatCurrency }) {
  const chartData = useMemo(
    () =>
      account.points.map((point) => ({
        date: point.date,
        balance: pickBalanceValue(point, balanceMode),
      })),
    [account.points, balanceMode]
  );
  const { latest, previousMonth } = getHighlightDates(account.points);
  const dateSet = new Set(chartData.map((point) => point.date));
  const showPreviousMonth = previousMonth && dateSet.has(previousMonth);

  return (
    <Box mt={6}>
      <Text fontWeight="semibold" mb={3}>
        {accountLabel(account)}
        {account.accountName ? ` - ${account.accountName}` : ''}
      </Text>
      <ResponsiveContainer width="99%" height={220}>
        <LineChart data={chartData} margin={{ top: 16, right: 24, bottom: 8, left: 8 }}>
          <CartesianGrid stroke="#E0E8F0" vertical={false} />
          <XAxis
            dataKey="date"
            stroke="#E0E8F0"
            tick={{ fill: '#4A5568', fontSize: 11 }}
            minTickGap={28}
            tickFormatter={formatAxisDate}
          />
          <YAxis
            axisLine={false}
            width={56}
            tick={{ fill: '#4A5568', fontSize: 12 }}
            tickFormatter={formatBalanceThousands}
          />
          <Tooltip
            formatter={(value) => [formatCurrency(value), 'Balance']}
            labelFormatter={(label) => label}
          />
          {showPreviousMonth && (
            <ReferenceLine
              x={previousMonth}
              stroke={PREVIOUS_MONTH_LINE_COLOR}
              strokeDasharray="4 4"
              label={{
                value: 'Previous month',
                position: 'insideTopLeft',
                fontSize: 11,
                fill: PREVIOUS_MONTH_LINE_COLOR,
              }}
            />
          )}
          {latest && dateSet.has(latest) && (
            <ReferenceLine
              x={latest}
              stroke={LATEST_LINE_COLOR}
              strokeDasharray="3 3"
              label={{
                value: 'Latest',
                position: 'insideTopRight',
                fontSize: 11,
                fill: LATEST_LINE_COLOR,
              }}
            />
          )}
          <Line
            type="monotone"
            dataKey="balance"
            stroke={LINE_COLOR}
            strokeWidth={2}
            dot={false}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

AccountBalanceChart.propTypes = {
  account: PropTypes.shape({
    bankName: PropTypes.string,
    lastFour: PropTypes.string,
    currencyCode: PropTypes.string,
    accountName: PropTypes.string,
    points: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string.isRequired,
        current_balance: PropTypes.number,
        available_balance: PropTypes.number,
      })
    ).isRequired,
  }).isRequired,
  balanceMode: PropTypes.oneOf([BALANCE_MODE.CURRENT, BALANCE_MODE.AVAILABLE])
    .isRequired,
  formatCurrency: PropTypes.func.isRequired,
};

function AccountBalanceCharts({ dailyBalances, selectedAccountIds, formatCurrency }) {
  const [balanceMode, setBalanceMode] = useState(BALANCE_MODE.CURRENT);
  const accounts = useMemo(
    () => groupDailyBalancesByAccount(dailyBalances, selectedAccountIds),
    [dailyBalances, selectedAccountIds]
  );

  if (accounts.length === 0) {
    return null;
  }

  return (
    <Card mt={10} mb={6}>
      <Flex justify="space-between" align="center" gap={3} flexWrap="wrap">
        <Heading variant={'sectionHeader'} borderColor={'twelve.green.300'}>
          Daily Balances
        </Heading>
        <ButtonGroup size="sm" isAttached variant="outline">
          <Button
            onClick={() => setBalanceMode(BALANCE_MODE.CURRENT)}
            colorScheme={balanceMode === BALANCE_MODE.CURRENT ? 'brand' : undefined}
            variant={balanceMode === BALANCE_MODE.CURRENT ? 'solid' : 'outline'}
          >
            Current
          </Button>
          <Button
            onClick={() => setBalanceMode(BALANCE_MODE.AVAILABLE)}
            colorScheme={
              balanceMode === BALANCE_MODE.AVAILABLE ? 'brand' : undefined
            }
            variant={
              balanceMode === BALANCE_MODE.AVAILABLE ? 'solid' : 'outline'
            }
          >
            Available
          </Button>
        </ButtonGroup>
      </Flex>
      {accounts.map((account) => (
        <AccountBalanceChart
          key={account.accountId}
          account={account}
          balanceMode={balanceMode}
          formatCurrency={formatCurrency}
        />
      ))}
    </Card>
  );
}

AccountBalanceCharts.propTypes = {
  dailyBalances: PropTypes.array,
  selectedAccountIds: PropTypes.arrayOf(PropTypes.string),
  formatCurrency: PropTypes.func.isRequired,
};

AccountBalanceCharts.defaultProps = {
  dailyBalances: [],
  selectedAccountIds: [],
};

export default AccountBalanceCharts;
